import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AngularFirestore } from 'angularfire2/firestore';
import { AngularFireAuth } from 'angularfire2/auth';
import { BaseObject } from '../shared/baseClass';
import { AuthService, User } from '../user/auth.service';
import { Guest } from '../website-builder/wedding';
import { Http, RequestOptions } from '@angular/http';
import { SettingsStateStore } from '../settings/settings.state.store';
import { Settings } from '../settings/settings';
import { MerchantStateMachine, MerchantState, MerchantEvent, getStateMessage } from '../shared/machines/merchant.state.machine';
import { Registry } from '../registry/registry';

export interface State {
  merchant: Merchant | null;
  userId: string | null;
}

export interface Merchant {
  merchantId: string;
  dateUpdate: number;
  status: string;
  updatedDate: number;
  createdDate: number;
  userId: string;
  schemaCountry: string;
  schemaCurrency: string; 
  personaSessionToken?: string;
  personaInquiryId?: string;
  personaTimestamp?: number;
  dataMismatchErrors?: string[];
  maxTransfersOverride?: number;
  transfers?: Array<{
    transferId: string;
    date: number;
  }>;
}

interface Transfer {
  transferId: string;
  createdAt: string;
  userId: string;
  amount: number;
  currency: string;
  status: string;
  transferDate: string;
}

@Injectable()
export class MerchantStateStore {
  private readonly FIRESTORE_MERCHANTS_COLLECTION = 'merchants';
  private _state$ = new BehaviorSubject<State>({
    merchant: null,
    userId: null
  });
  private _stateMachine: MerchantStateMachine = new MerchantStateMachine(MerchantState.INITIAL);

  constructor(
    private firestore: AngularFirestore,
    private authService: AuthService
  ) {}

  get state$(): Observable<State> {
    return this._state$.asObservable();
  }

  private setState(state: Partial<State>) {
    this._state$.next({
      ...this._state$.getValue(),
      ...state
    });
  }

  private getCurrentState(): State {
    return this._state$.getValue();
  }

  getMerchant(userId: string): Observable<Merchant | null> {
    return this.firestore.collection<Merchant>(
      this.FIRESTORE_MERCHANTS_COLLECTION,
      ref => ref.where('userId', '==', userId)
    )
    .valueChanges()
    .pipe(
      map(merchants => {
        const merchant = merchants && merchants.length > 0 ? merchants[0] : null;
        if (merchant) {
          this._stateMachine = new MerchantStateMachine(merchant.status as MerchantState);
          this.setState({
            merchant,
            userId
          });
        }
        return merchant;
      })
    );
  }

  get stateMachine() {
    return this._stateMachine;
  }

  getStateMessage(): string {
    return getStateMessage(this._stateMachine.getState());
  }

  createMerchant(merchantId: string, country: string, currency: string, userId: string) {
    const currentState = this.getCurrentState();
   

    if (!merchantId || !userId) {
      throw new Error('merchantId and userId are required to create merchant document');
    }

    this._stateMachine = new MerchantStateMachine(MerchantState.INITIAL);


    return this.firestore.collection<Merchant>(
      this.FIRESTORE_MERCHANTS_COLLECTION, 
      ref => ref.where('userId', '==', userId)
    )
    .snapshotChanges()
    .pipe(take(1))
    .subscribe(merchants => {
      if (merchants && merchants.length > 0) {
        console.log('Merchant already exists for this user');
        return;
      }

      const now = Date.now();
      const merchant: Merchant = {
        merchantId,
        dateUpdate: now,
        status: MerchantState.SUBMITTED,
        updatedDate: now,
        createdDate: now,
        userId,
        schemaCountry: country,
        schemaCurrency: currency,
        personaSessionToken: null,
        personaInquiryId: null,
        personaTimestamp: null
      };

      this.firestore.collection(this.FIRESTORE_MERCHANTS_COLLECTION)
        .doc(merchantId)
        .set(merchant)
        .then(() => {
          this._stateMachine.transition(MerchantEvent.SUBMISSION_SUCCESS);
          this.updateMerchant({
            status: this._stateMachine.getState()
          }, userId);
        })
        .catch(error => {
          console.error('Error creating merchant:', error);
          this.handleSubmissionError();
        });

        //set registry currenty and country, get the registry doc where userId is the same as the userId
        this.firestore.collection('registries').ref.where('userId', '==', userId).get().then(snapshot => {
          if (snapshot.docs.length > 0) {
            const registry = snapshot.docs[0].data() as Registry;
            registry.currency = currency;
            registry.country = country;
            this.firestore.collection('registries').doc(snapshot.docs[0].id).set(registry, { merge: true });
          }
        });
    });
  }


  async getTransfers(userId: string): Promise<Array<{transferId: string, date: number}>> {
    const transfersRef = this.firestore.collection<Transfer>('transfers', ref => 
      ref.where('userId', '==', userId)
    );
    
    const transfers = await transfersRef.valueChanges()
      .pipe(take(1))
      .toPromise();
    
    if (!transfers || transfers.length === 0) {
      return [];
    }
    
    return transfers.map(transfer => ({
      transferId: transfer.transferId,
      amount: transfer.amount,
      currency: transfer.currency,
      status: transfer.status,
      date: new Date(transfer.createdAt).getTime(),
      
      transferDate: new Date(new Date(transfer.transferDate).getTime() + 5 * 24 * 60 * 60 * 1000) //4 days after the transfer date
    }));
  }

  async updateMerchant(updates: Partial<Merchant>, userId: string) {
    const currentMerchant = await this.getMerchant(userId)
      .pipe(take(1))
      .toPromise();
      
    if (!currentMerchant) return;

    const updatedMerchant = {
      ...currentMerchant,
      ...updates,
      updatedDate: Date.now(),
      personaSessionToken: updates.personaSessionToken === null ? null : (updates.personaSessionToken || currentMerchant.personaSessionToken),
      personaInquiryId: updates.personaInquiryId === null ? null : (updates.personaInquiryId || currentMerchant.personaInquiryId),
      personaTimestamp: updates.personaTimestamp === null ? null : (updates.personaTimestamp || currentMerchant.personaTimestamp)
    };

    return this.firestore.collection(this.FIRESTORE_MERCHANTS_COLLECTION)
      .doc(currentMerchant.merchantId)
      .set(updatedMerchant, { merge: true })
      .then(() => {
        const currentState = this.getCurrentState();
        currentState.merchant = updatedMerchant;
        this.setState(currentState);
        return updatedMerchant;
      });
  }

  // Add methods to send events
  submitDetails() {
    this._stateMachine.transition(MerchantEvent.SUBMIT_DETAILS);
  }

  handleSubmissionSuccess() {
    this._stateMachine.transition(MerchantEvent.SUBMISSION_SUCCESS);
  }

  handleSubmissionError() {
    this._stateMachine.transition(MerchantEvent.SUBMISSION_ERROR);
  }

  submitKyc() {
    this._stateMachine.transition(MerchantEvent.KYC_CREATE);
  }

  handleKycStarted() {
    this._stateMachine.transition(MerchantEvent.KYC_START);
  }

 
  handleKycFailed() {
    this._stateMachine.transition(MerchantEvent.KYC_FAIL);
  }

  handleKycExpired() {
    this._stateMachine.transition(MerchantEvent.KYC_EXPIRE);
  }

 

  approve() {
    this._stateMachine.transition(MerchantEvent.KYC_APPROVE);
  }

  decline() {
    this._stateMachine.transition(MerchantEvent.KYC_DECLINE);
  }



  // ... other event handler methods
} 


// WEBPACK FOOTER //
// ./src/app/merchant/merchant.state.store.ts